import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, ShoppingCart, Truck, Shield, RefreshCw, ChevronRight, Minus, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useCart } from '../context/CartContext';
import { getProduct, getReviews, getProducts, createReview } from '../lib/api';
import { toast } from 'sonner';
import ProductCard from '../components/ProductCard';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const { addItem } = useCart();

  const [newReview, setNewReview] = useState({
    customer_name: '',
    rating: 5,
    title: '',
    comment: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const productRes = await getProduct(slug);
        setProduct(productRes.data);
        
        const [reviewsRes, relatedRes] = await Promise.all([
          getReviews(productRes.data.id),
          getProducts({ category: productRes.data.category_id, limit: 4 })
        ]);
        
        setReviews(reviewsRes.data);
        setRelatedProducts(relatedRes.data.products.filter(p => p.id !== productRes.data.id).slice(0, 4));
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Product not found');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    setQuantity(1);
    setSelectedImage(0);
  }, [slug]);

  const handleAddToCart = () => {
    if (product) {
      addItem(product, quantity);
      toast.success(`${quantity} x ${product.name} added to cart`);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!newReview.customer_name || !newReview.title || !newReview.comment) {
      toast.error('Please fill in all fields');
      return;
    }
    setSubmittingReview(true);
    try {
      await createReview({
        product_id: product.id,
        ...newReview
      });
      toast.success('Review submitted successfully!');
      const reviewsRes = await getReviews(product.id);
      setReviews(reviewsRes.data);
      setNewReview({ customer_name: '', rating: 5, title: '', comment: '' });
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] py-8">
        <div className="max-w-[1440px] mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="aspect-square bg-slate-200 rounded-lg animate-pulse" />
            <div className="space-y-4">
              <div className="h-8 bg-slate-200 rounded w-3/4 animate-pulse" />
              <div className="h-6 bg-slate-200 rounded w-1/4 animate-pulse" />
              <div className="h-24 bg-slate-200 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] py-8">
        <div className="max-w-[1440px] mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Product not found</h1>
          <Link to="/products">
            <Button>Browse Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  const discount = product.original_price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : null;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-[1440px] mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/" className="hover:text-[#003366]">Home</Link>
            <ChevronRight size={14} />
            <Link to="/products" className="hover:text-[#003366]">Products</Link>
            <ChevronRight size={14} />
            <Link to={`/products?category=${product.category_id}`} className="hover:text-[#003366]">
              {product.category_name}
            </Link>
            <ChevronRight size={14} />
            <span className="text-slate-900">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Images */}
          <div>
            <div className="aspect-square bg-white rounded-lg border overflow-hidden mb-4">
              <img
                src={product.images?.[selectedImage] || '/placeholder.png'}
                alt={product.name}
                className="w-full h-full object-contain"
                data-testid="product-main-image"
              />
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-2">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`w-20 h-20 border rounded-lg overflow-hidden ${
                      selectedImage === i ? 'border-[#003366] border-2' : 'border-slate-200'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product info */}
          <div>
            {product.deal && (
              <span className="inline-block bg-[#FFD700] text-slate-900 text-xs font-bold px-3 py-1 rounded mb-3">
                DEAL
              </span>
            )}
            
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-testid="product-name">
              {product.name}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={18}
                    className={star <= Math.round(product.rating) ? 'fill-[#FFD700] text-[#FFD700]' : 'text-slate-300'}
                  />
                ))}
              </div>
              <span className="text-slate-600">
                {product.rating.toFixed(1)} ({product.review_count} reviews)
              </span>
            </div>

            {/* Price */}
            <div className="mb-6">
              <div className="flex items-baseline gap-3">
                <span className="text-3xl font-bold text-[#B12704]" data-testid="product-price">
                  ${product.price.toFixed(2)}
                </span>
                {product.original_price && (
                  <>
                    <span className="text-xl text-slate-400 line-through">
                      ${product.original_price.toFixed(2)}
                    </span>
                    <span className="bg-[#10B981] text-white text-sm font-bold px-2 py-1 rounded">
                      Save {discount}%
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Description */}
            <p className="text-slate-600 mb-6">{product.description}</p>

            {/* Stock */}
            <p className={`mb-6 font-semibold ${product.stock > 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
              {product.stock > 0 ? `In Stock (${product.stock} available)` : 'Out of Stock'}
            </p>

            {/* Quantity and Add to Cart */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex items-center border rounded-lg">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-3 hover:bg-slate-100 transition-colors"
                  data-testid="decrease-qty"
                >
                  <Minus size={18} />
                </button>
                <span className="px-6 py-3 font-semibold" data-testid="quantity-display">{quantity}</span>
                <button
                  onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                  className="px-4 py-3 hover:bg-slate-100 transition-colors"
                  data-testid="increase-qty"
                >
                  <Plus size={18} />
                </button>
              </div>
              
              <Button
                onClick={handleAddToCart}
                disabled={product.stock === 0}
                className="flex-1 bg-[#FFD700] hover:bg-[#F4C430] text-slate-900 font-bold py-6 text-lg rounded-full"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart size={20} className="mr-2" />
                Add to Cart
              </Button>
            </div>

            {/* Benefits */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="flex flex-col items-center text-center">
                <Truck className="text-[#003366] mb-1" size={24} />
                <span className="text-xs text-slate-600">Free Shipping</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <Shield className="text-[#003366] mb-1" size={24} />
                <span className="text-xs text-slate-600">Secure Payment</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <RefreshCw className="text-[#003366] mb-1" size={24} />
                <span className="text-xs text-slate-600">Easy Returns</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs for Description and Reviews */}
        <Tabs defaultValue="reviews" className="mb-12">
          <TabsList className="w-full justify-start bg-white border-b rounded-none">
            <TabsTrigger value="description" className="data-[state=active]:border-b-2 data-[state=active]:border-[#003366]">
              Description
            </TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:border-b-2 data-[state=active]:border-[#003366]">
              Reviews ({reviews.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="description" className="bg-white rounded-b-lg p-6">
            <p className="text-slate-600">{product.description}</p>
          </TabsContent>
          
          <TabsContent value="reviews" className="bg-white rounded-b-lg p-6">
            {/* Review form */}
            <div className="mb-8 p-4 bg-slate-50 rounded-lg">
              <h3 className="font-semibold text-slate-900 mb-4">Write a Review</h3>
              <form onSubmit={handleSubmitReview} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    placeholder="Your name"
                    value={newReview.customer_name}
                    onChange={(e) => setNewReview(prev => ({ ...prev, customer_name: e.target.value }))}
                    data-testid="review-name-input"
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">Rating:</span>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setNewReview(prev => ({ ...prev, rating: star }))}
                      >
                        <Star
                          size={24}
                          className={star <= newReview.rating ? 'fill-[#FFD700] text-[#FFD700]' : 'text-slate-300'}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <Input
                  placeholder="Review title"
                  value={newReview.title}
                  onChange={(e) => setNewReview(prev => ({ ...prev, title: e.target.value }))}
                  data-testid="review-title-input"
                />
                <Textarea
                  placeholder="Your review"
                  value={newReview.comment}
                  onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
                  rows={3}
                  data-testid="review-comment-input"
                />
                <Button
                  type="submit"
                  disabled={submittingReview}
                  className="bg-[#003366] hover:bg-[#002855]"
                  data-testid="submit-review-btn"
                >
                  {submittingReview ? 'Submitting...' : 'Submit Review'}
                </Button>
              </form>
            </div>

            {/* Reviews list */}
            {reviews.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No reviews yet. Be the first to review!</p>
            ) : (
              <div className="space-y-6">
                {reviews.map((review) => (
                  <div key={review.id} className="border-b pb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            size={14}
                            className={star <= review.rating ? 'fill-[#FFD700] text-[#FFD700]' : 'text-slate-300'}
                          />
                        ))}
                      </div>
                      <span className="font-semibold text-slate-900">{review.title}</span>
                    </div>
                    <p className="text-slate-600 mb-2">{review.comment}</p>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <span>{review.customer_name}</span>
                      {review.verified && (
                        <span className="bg-[#10B981]/10 text-[#10B981] px-2 py-0.5 rounded text-xs">
                          Verified Purchase
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Related Products</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedProducts.map((prod) => (
                <ProductCard key={prod.id} product={prod} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
